import { useReadContract } from 'wagmi'
import { Address, formatUnits } from 'viem'
import { accountLensAbi } from 'abis/evm/accountLens'
import { ACCOUNT_LENS_ADDRESS } from '@/config/euler-contracts'

/**
 * Raw liquidity info from the account lens contract
 */
export interface AccountLiquidityInfo {
  /** Whether the liquidity query failed */
  queryFailure: boolean
  /** Reason for query failure (if any) */
  queryFailureReason: `0x${string}`
  /** The account address */
  account: Address
  /** The liability vault (controller) */
  vault: Address
  /** Unit of account for values (typically USD with 18 decimals) */
  unitOfAccount: Address
  /** Time until liquidation (negative = safe, positive = time in seconds, special values for errors) */
  timeToLiquidation: bigint
  /** Total liability value for borrowing calculations */
  liabilityValueBorrowing: bigint
  /** Total liability value for liquidation calculations */
  liabilityValueLiquidation: bigint
  /** Total collateral value weighted by borrow LTVs (across all enabled collaterals) */
  collateralValueBorrowing: bigint
  /** Total collateral value weighted by liquidation LTVs (across all enabled collaterals) */
  collateralValueLiquidation: bigint
  /** Total raw/unweighted collateral value */
  collateralValueRaw: bigint
  /** Array of enabled collateral vault addresses */
  collaterals: readonly Address[]
  /** Per-collateral values weighted by borrow LTV */
  collateralValuesBorrowing: readonly bigint[]
  /** Per-collateral values weighted by liquidation LTV */
  collateralValuesLiquidation: readonly bigint[]
  /** Per-collateral raw/unweighted values */
  collateralValuesRaw: readonly bigint[]
}

/**
 * Computed account liquidity metrics
 */
export interface AccountLiquidityMetrics {
  /**
   * Health factor: collateralValueLiquidation / liabilityValueLiquidation
   * - > 1.0 = healthy (cannot be liquidated)
   * - < 1.0 = can be liquidated
   * - undefined = no debt (infinite health)
   */
  healthFactor: number | undefined
  /**
   * Current LTV as percentage: (liabilityValue / collateralValueRaw) * 100
   * This shows how much of the collateral is being borrowed against
   */
  currentLTV: number
  /**
   * Borrow capacity remaining as percentage of max borrow LTV
   * = (collateralValueBorrowing - liabilityValueBorrowing) / collateralValueBorrowing * 100
   */
  borrowCapacityUsed: number
  /**
   * Effective max LTV considering all collaterals weighted by their individual max LTVs
   * = (collateralValueBorrowing / collateralValueRaw) * 100
   * This is the weighted average max LTV across all enabled collaterals
   */
  effectiveMaxLTV: number
  /**
   * Effective liquidation LTV considering all collaterals weighted by their individual liquidation LTVs
   * = (collateralValueLiquidation / collateralValueRaw) * 100
   * This is the weighted average liquidation LTV across all enabled collaterals
   */
  effectiveLiquidationLTV: number
  /**
   * Max borrow capacity in USD (collateral value weighted by borrow LTVs)
   * Use this for accurate "max borrow" calculations with multiple collaterals
   */
  maxBorrowCapacityUSD: number
  /**
   * Total collateral value in USD (raw, unweighted)
   */
  totalCollateralValueUSD: number
  /**
   * Total debt value in USD (liquidation-weighted)
   */
  totalDebtValueUSD: number
  /**
   * Total debt value in USD (borrow-weighted, consistent with maxBorrowCapacityUSD)
   */
  totalDebtBorrowUSD: number
  /**
   * Net value (collateral - debt) in USD
   */
  netValueUSD: number
  /**
   * Whether the account has any debt
   */
  hasDebt: boolean
  /**
   * Whether the account is in a liquidatable state
   */
  isLiquidatable: boolean
  /**
   * Raw liquidity info from the contract
   */
  raw: AccountLiquidityInfo | undefined
}

export interface UseAccountLiquidityParams {
  /** The sub-account address to query */
  accountAddress?: Address
  /** The liability vault (controller) to check liquidity against */
  liabilityVault?: Address
  /** Chain ID */
  chainId: number
  /** Whether the query is enabled */
  enabled?: boolean
}

export interface UseAccountLiquidityResult {
  /** Computed liquidity metrics */
  data: AccountLiquidityMetrics | undefined
  /** Whether the query is loading */
  isLoading: boolean
  /** Whether the query has an error */
  isError: boolean
  /** Error object if any */
  error: Error | null
  /** Refetch function */
  refetch: () => void
}

/**
 * Hook to fetch account-wide liquidity information from Euler's Account Lens
 *
 * This provides accurate health factor and LTV calculations that consider ALL
 * enabled collaterals for a sub-account, not just a single collateral vault.
 *
 * @example
 * ```tsx
 * const { data } = useAccountLiquidity({
 *   accountAddress: subAccountAddress,
 *   liabilityVault: liabilityVaultAddress,
 *   chainId: 999,
 *   enabled: !!subAccountAddress && !!liabilityVaultAddress,
 * })
 *
 * // Use the computed metrics
 * console.log(data?.healthFactor) // e.g., 1.85
 * console.log(data?.currentLTV) // e.g., 45.5 (percent)
 * ```
 */
export function useAccountLiquidity({
  accountAddress,
  liabilityVault,
  chainId,
  enabled = true,
}: UseAccountLiquidityParams): UseAccountLiquidityResult {
  const accountLensAddress = ACCOUNT_LENS_ADDRESS

  const {
    data: accountInfo,
    isLoading,
    isError,
    error,
    refetch,
  } = useReadContract({
    address: accountLensAddress,
    abi: accountLensAbi,
    functionName: 'getAccountInfo',
    args: accountAddress && liabilityVault ? [accountAddress, liabilityVault] : undefined,
    chainId,
    query: {
      enabled: enabled && !!accountAddress && !!liabilityVault && !!accountLensAddress,
      staleTime: 30_000, // 30 seconds
      refetchInterval: 30_000,
    },
  })

  // Parse and compute metrics from the raw response
  const data: AccountLiquidityMetrics | undefined = (() => {
    if (!accountInfo) return undefined

    const liquidityInfo = accountInfo.vaultAccountInfo.liquidityInfo

    // If query failed, return undefined so downstream ?? fallbacks work correctly
    // (returning 0 would prevent fallback since 0 ?? value evaluates to 0)
    if (liquidityInfo.queryFailure) {
      return undefined
    }

    // Values are in the unit of account (typically USD with 18 decimals)
    const liabilityValueLiq = liquidityInfo.liabilityValueLiquidation
    const liabilityValueBorrow = liquidityInfo.liabilityValueBorrowing
    const collateralValueLiq = liquidityInfo.collateralValueLiquidation
    const collateralValueBorrow = liquidityInfo.collateralValueBorrowing
    const collateralValueRaw = liquidityInfo.collateralValueRaw

    // Convert to numbers (assuming 18 decimals for unit of account)
    const debtUSD = Number(formatUnits(liabilityValueLiq, 18))
    const collateralUSD = Number(formatUnits(collateralValueRaw, 18))

    const hasDebt = liabilityValueLiq > 0n

    // Health factor = collateralValueLiquidation / liabilityValueLiquidation
    // undefined if no debt (infinite health)
    const healthFactor = hasDebt
      ? Number(formatUnits(collateralValueLiq, 18)) / Number(formatUnits(liabilityValueLiq, 18))
      : undefined

    // Current LTV = (debt / raw collateral) * 100
    const currentLTV = collateralUSD > 0 ? (debtUSD / collateralUSD) * 100 : 0

    // Borrow capacity used = liability / collateralValueBorrowing * 100
    const collateralBorrowUSD = Number(formatUnits(collateralValueBorrow, 18))
    const liabilityBorrowUSD = Number(formatUnits(liabilityValueBorrow, 18))
    const borrowCapacityUsed = collateralBorrowUSD > 0 ? (liabilityBorrowUSD / collateralBorrowUSD) * 100 : 0

    // Effective max LTV = weighted average of all collateral max LTVs
    // = collateralValueBorrowing / collateralValueRaw * 100
    const effectiveMaxLTV = collateralUSD > 0 ? (collateralBorrowUSD / collateralUSD) * 100 : 0

    // Effective liquidation LTV = weighted average of all collateral liquidation LTVs
    // = collateralValueLiquidation / collateralValueRaw * 100
    const collateralLiqUSD = Number(formatUnits(collateralValueLiq, 18))
    const effectiveLiquidationLTV = collateralUSD > 0 ? (collateralLiqUSD / collateralUSD) * 100 : 0

    // Is liquidatable when health factor < 1
    const isLiquidatable = hasDebt && healthFactor !== undefined && healthFactor < 1

    return {
      healthFactor,
      currentLTV,
      borrowCapacityUsed,
      effectiveMaxLTV,
      effectiveLiquidationLTV,
      maxBorrowCapacityUSD: collateralBorrowUSD,
      totalCollateralValueUSD: collateralUSD,
      totalDebtValueUSD: debtUSD,
      totalDebtBorrowUSD: liabilityBorrowUSD,
      netValueUSD: collateralUSD - debtUSD,
      hasDebt,
      isLiquidatable,
      raw: liquidityInfo as AccountLiquidityInfo,
    }
  })()

  return {
    data,
    isLoading,
    isError,
    error: error as Error | null,
    refetch,
  }
}

/**
 * Get the account lens address for a chain
 */
export function getAccountLensAddress(_chainId: number): Address | undefined {
  return ACCOUNT_LENS_ADDRESS
}
